import React from "react"
import MfaRoute from "../components/MfaRoute/mfaRoute"
import NotConnectedRoute from "../components/NotConnectedRoute/notConnectedRoute"
import PermissionRoute from "../components/PermissionRoute/permissionRoute"

/**
 * Permet de créer des routes wrapper avec le composant NotConnectedRoute dynamiquement
 */
export const createNotConnectedRouteProps = (
    pageArchitecture,
    homeRoute,
    page,
    Component,
    options,
    defaultSelectors,
) => {
    const {pathKey = "route", wrapper, connectedUserRoutePage, connectedUserRoutePathKey = "route"} = options || {}
    const path = pageArchitecture[page]?.[pathKey]
    if (!path) return null

    const connectedUserRoute = connectedUserRoutePage
        ? pageArchitecture[connectedUserRoutePage]?.[connectedUserRoutePathKey] ?? homeRoute
        : homeRoute

    const child = wrapper ? wrapper(<Component />) : <Component />
    return {
        key: path,
        path,
        element: (
            <NotConnectedRoute connectedUserRoute={connectedUserRoute} {...defaultSelectors}>
                {child}
            </NotConnectedRoute>
        ),
    }
}

/**
 * Permet de créer des routes wrapper avec le composant MfaRoute dynamiquement
 */
const createMfaRouteProps = (
    pageArchitecture,
    homeRoute,
    loginRoute,
    hasMfaSessionId,
    isUserConnected,
    page,
    Component,
    defaultSelectors,
) => {
    const path = pageArchitecture[page]?.route
    if (!path) return null

    return {
        key: path,
        path,
        element: (
            <MfaRoute
                connectedUserRoute={homeRoute}
                hasMfaSessionId={hasMfaSessionId}
                isUserConnected={isUserConnected}
                noMfaSessionIdUserRoute={loginRoute}
                {...defaultSelectors}
            >
                <Component />
            </MfaRoute>
        ),
    }
}

/**
 * Permet de créer des routes simple sans wrapper dynamiquement
 */
const createSimpleRouteProps = (pageArchitecture, page, Component, options = {}) => {
    const {pathOverride, pathKey = "route"} = options
    const path = pathOverride ?? (page ? pageArchitecture[page]?.[pathKey] : null)
    if (!path) return null

    return {
        key: path,
        path,
        element: <Component />,
    }
}

/**
 * Permet de créer des routes wrapper avec le composant PermissionRoute dynamiquement
 */
const createPermissionRouteProps = (
    pageArchitecture,
    loginRoute,
    unauthorizedRoute,
    page,
    Component,
    options,
    defaultSelectors,
) => {
    const {pathKey = "route", pathOverride, permissionRoutePath, permissionRouteOverrides = {}} = options || {}

    const path = pathOverride ?? (page ? pageArchitecture[page]?.[pathKey] : null)
    const permissionPath = permissionRoutePath ?? path

    if (!path) return null

    return {
        key: path,
        path,
        element: (
            <PermissionRoute
                loginRoute={loginRoute}
                path={permissionPath}
                permissions={page ? pageArchitecture[page]?.permissions : undefined}
                unauthorizedRoute={unauthorizedRoute}
                {...defaultSelectors}
                {...permissionRouteOverrides}
            >
                <Component />
            </PermissionRoute>
        ),
    }
}

/**
 * Permet de créer des routes dynamiquement avec n'importe quel wrapper
 */
export const buildRoutesFromConfig = (config, defaultSelectors, createProps) =>
    config
        .map(entry => {
            const {page, component, ...options} = entry
            return createProps(page, component, options, defaultSelectors)
        })
        .filter(Boolean)

/**
 * Permet de créer des routes wrapper avec le composant PermissionRoute dynamiquement
 */
export const buildPermissionRoutesFromConfig = (
    pageArchitecture,
    loginRoute,
    unauthorizedRoute,
    config,
    defaultSelectors,
) =>
    buildRoutesFromConfig(config, defaultSelectors, (page, Component, options, selectors) =>
        createPermissionRouteProps(
            pageArchitecture,
            loginRoute,
            unauthorizedRoute,
            page,
            Component,
            options,
            selectors,
        ),
    )

/**
 * Permet de créer des routes wrapper avec le composant NotConnectedRoute dynamiquement
 */
export const buildNotConnectedRoutesFromConfig = (pageArchitecture, homeRoute, notConnectedRoutes, defaultSelectors) =>
    buildRoutesFromConfig(notConnectedRoutes, defaultSelectors, (page, Component, options, selectors) =>
        createNotConnectedRouteProps(pageArchitecture, homeRoute, page, Component, options, selectors),
    )

/**
 * Permet de créer des routes wrapper avec le composant MfaRoute dynamiquement
 */
export const buildMfaRoutesFromConfig = (
    pageArchitecture,
    homeRoute,
    loginRoute,
    hasMfaSessionId,
    isUserConnected,
    mfaRoutes,
    defaultSelectors,
) =>
    mfaRoutes
        .map(({page, component}) =>
            createMfaRouteProps(
                pageArchitecture,
                homeRoute,
                loginRoute,
                hasMfaSessionId,
                isUserConnected,
                page,
                component,
                defaultSelectors,
            ),
        )
        .filter(Boolean)

/**
 * Permet de créer des routes simple sans wrapper dynamiquement
 */
export const buildSimpleRoutesFromConfig = (pageArchitecture, config) =>
    config
        .map(entry => {
            const {page, component, ...options} = entry
            return createSimpleRouteProps(pageArchitecture, page, component, options)
        })
        .filter(Boolean)
